import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    Search,
    FilterList,
    Download,
    SwapHoriz,
    Receipt,
    QrCode,
    Link,
    PointOfSale,
    CheckCircle,
    Pending,
    Error,
    Schedule,
    AccountBalanceWallet
} from '@mui/icons-material';
import TransactionModal from '../../components/admin/transactions/TransactionModal';
import { resolveOperator } from '../../utils/operator';

const TransactionStats = ({ transactions }) => {
    const stats = {
        total: transactions.length,
        completed: transactions.filter(t => t.status === 'completed').length,
        pending: transactions.filter(t => t.status === 'pending').length,
        failed: transactions.filter(t => t.status === 'failed').length,
        totalAmount: transactions.reduce((sum, t) => sum + (t.status === 'completed' ? t.amount : 0), 0)
    };

    // Calculer les pourcentages pour les barres de progression
    const completedPercentage = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;
    const pendingPercentage = stats.total > 0 ? (stats.pending / stats.total) * 100 : 0;
    const failedPercentage = stats.total > 0 ? (stats.failed / stats.total) * 100 : 0;

    const statCards = [
        {
            label: 'Total',
            value: stats.total,
            icon: SwapHoriz,
            gradient: 'from-blue-500 via-indigo-500 to-blue-600',
            bgGradient: 'from-blue-50 via-indigo-50 to-blue-50',
            iconBg: 'from-blue-500 to-indigo-600',
            borderColor: 'border-blue-200/50',
            textColor: 'text-blue-700',
            valueColor: 'text-blue-900',
            percentage: 100,
            description: 'Toutes les trans.'
        },
        {
            label: 'Terminées',
            value: stats.completed,
            icon: CheckCircle,
            gradient: 'from-emerald-500 via-green-500 to-teal-600',
            bgGradient: 'from-emerald-50 via-green-50 to-teal-50',
            iconBg: 'from-emerald-500 to-teal-600',
            borderColor: 'border-emerald-200/50',
            textColor: 'text-emerald-700',
            valueColor: 'text-emerald-600',
            percentage: completedPercentage,
            description: `${completedPercentage.toFixed(0)}% du total`
        },
        {
            label: 'En Attente',
            value: stats.pending,
            icon: Pending,
            gradient: 'from-amber-500 via-orange-500 to-amber-600',
            bgGradient: 'from-amber-50 via-orange-50 to-amber-50',
            iconBg: 'from-amber-500 to-orange-600',
            borderColor: 'border-amber-200/50',
            textColor: 'text-amber-700',
            valueColor: 'text-amber-600',
            percentage: pendingPercentage,
            description: `${pendingPercentage.toFixed(0)}% du total`
        },
        {
            label: 'Montant',
            value: null,
            icon: Receipt,
            gradient: 'from-purple-500 via-violet-500 to-fuchsia-600',
            bgGradient: 'from-purple-50 via-violet-50 to-fuchsia-50',
            iconBg: 'from-purple-500 to-fuchsia-600',
            borderColor: 'border-purple-200/50',
            textColor: 'text-purple-700',
            valueColor: 'text-purple-600',
            amount: stats.totalAmount,
            currency: 'CDF',
            description: 'Trans. complétées',
            showAmount: true
        }
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                    <div
                        key={index}
                        className={`group relative bg-gradient-to-br ${stat.bgGradient} rounded-3xl p-5 border-2 ${stat.borderColor} shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden min-h-[160px] flex flex-col`}
                    >
                        {/* Effet de fond animé */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                        
                        <div className="relative z-10 flex flex-col flex-1">
                            <div className="flex items-start justify-between mb-3">
                                <div className={`p-3 bg-gradient-to-br ${stat.iconBg} rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500 flex-shrink-0`}>
                                    <Icon className="text-white text-xl" />
                                </div>
                                {stat.percentage !== undefined && stat.percentage < 100 && stat.label !== 'Total Transactions' && (
                                    <div className="text-right flex-shrink-0">
                                        <div className="text-xs font-bold text-gray-700 mb-1">
                                            {stat.percentage.toFixed(0)}%
                                        </div>
                                        <div className="w-12 h-1 bg-gray-200 rounded-full overflow-hidden">
                                            <div 
                                                className={`h-full bg-gradient-to-r ${stat.gradient} rounded-full transition-all duration-1000 ease-out`}
                                                style={{ width: `${stat.percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <div className="mb-3 flex-shrink-0">
                                <p className={`text-2xl lg:text-3xl font-bold ${stat.valueColor} transition-colors duration-300 leading-tight`}>
                                    {stat.showAmount && stat.amount !== undefined ? (
                                        <span className="block break-words">
                                            <span className="text-xl lg:text-2xl">{stat.amount.toLocaleString()}</span>
                                            <span className="text-sm lg:text-base font-normal ml-1">{stat.currency}</span>
                                        </span>
                                    ) : (
                                        <span className="text-2xl lg:text-3xl">{stat.value}</span>
                                    )}
                                </p>
                            </div>
                            
                            <div className="flex-1 flex flex-col justify-end">
                                <p className={`text-xs lg:text-sm font-bold ${stat.textColor} mb-1 leading-tight`}>
                                    {stat.label}
                                </p>
                                <p className="text-xs text-gray-600 leading-tight">{stat.description}</p>
                                
                                {/* Barre de progression en bas pour les statistiques avec pourcentage */}
                                {(stat.label !== 'Total Transactions' && stat.label !== 'Montant Total' && stat.percentage !== undefined) && (
                                    <div className="mt-3 h-1 bg-gray-200/50 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full bg-gradient-to-r ${stat.gradient} rounded-full transition-all duration-1000 ease-out`}
                                            style={{ width: `${stat.percentage}%` }}
                                        ></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Composant TransactionTabs
const TransactionTabs = ({ activeTab, onTabChange }) => {
    const tabs = [
        {
            id: 'all',
            label: 'Toutes les Transactions',
            icon: SwapHoriz,
            count: 1256,
            description: 'Vue globale de toutes les transactions'
        },
        {
            id: 'paymentRequests',
            label: 'Demandes de Paiement',
            icon: QrCode,
            count: 45,
            description: 'Requêtes de paiement en attente'
        },
        {
            id: 'paymentLinks',
            label: 'Liens de Paiement',
            icon: Link,
            count: 23,
            description: 'Liens de paiement générés'
        }
    ];

    return (
        <div className="bg-white rounded-2xl p-2 border border-gray-200 shadow-sm mb-6">
            <div className="flex flex-col lg:flex-row lg:space-x-2">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;

                    return (
                        <button
                            key={tab.id}
                            onClick={() => onTabChange(tab.id)}
                            className={`flex items-center space-x-3 p-4 rounded-xl transition-all duration-300 text-left flex-1 min-w-0 ${
                                isActive
                                    ? 'bg-gradient-to-r from-amber-50 to-cyan-10 border border-amber-200 shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-50 hover:border hover:border-gray-200'
                            }`}
                        >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                isActive
                                    ? 'bg-gradient-to-r from-amber-500 to-amber-500'
                                    : 'bg-gray-100'
                            }`}>
                                <Icon className={`text-lg ${
                                    isActive ? 'text-white' : 'text-gray-500'
                                }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                    <h4 className={`font-semibold ${
                                        isActive ? 'text-amber-900' : 'text-gray-900'
                                    }`}>
                                        {tab.label}
                                    </h4>
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                        isActive
                                            ? 'bg-amber-500 text-white'
                                            : 'bg-gray-200 text-gray-700'
                                    }`}>
                                        {tab.count}
                                    </span>
                                </div>
                                <p className={`text-sm mt-1 ${
                                    isActive ? 'text-amber-700' : 'text-gray-600'
                                }`}>
                                    {tab.description}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// Composant TransactionFilters
const TransactionFilters = ({ activeTab, onFilterChange }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);

    const getStatusOptions = () => {
        const baseOptions = [
            { value: 'all', label: 'Tous les statuts' },
            { value: 'completed', label: 'Complétés' },
            { value: 'pending', label: 'En attente' },
            { value: 'failed', label: 'Échoués' }
        ];

        return baseOptions;
    };

    const exportData = () => {
        alert(`Export des transactions en cours...`);
    };

    const handleFilterChange = (key, value) => {
        if (key === 'search') {
            setSearchTerm(value);
        } else if (key === 'status') {
            setStatusFilter(value);
        }

        if (onFilterChange) {
            onFilterChange({ search: key === 'search' ? value : searchTerm, status: key === 'status' ? value : statusFilter });
        }
    };

    return (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Rechercher une transaction..."
                        value={searchTerm}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                    />
                </div>

                <div className="flex items-center space-x-3">
                    <select
                        value={statusFilter}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                    >
                        {getStatusOptions().map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-colors"
                    >
                        <FilterList />
                        <span>Filtres</span>
                    </button>

                    <button
                        onClick={exportData}
                        className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl hover:from-blue-600 hover:to-cyan-600 transition-colors"
                    >
                        <Download />
                        <span>Exporter</span>
                    </button>
                </div>
            </div>

            {/* Filtres avancés */}
            {showFilters && (
                <div className="mt-4 p-4 bg-gray-50 rounded-2xl border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Montant minimum
                            </label>
                            <input
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200"
                                placeholder="0"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Montant maximum
                            </label>
                            <input
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200"
                                placeholder="1000000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Période
                            </label>
                            <select className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200">
                                <option>7 derniers jours</option>
                                <option>30 derniers jours</option>
                                <option>3 derniers mois</option>
                                <option>Personnalisé</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Composant TransactionRow
const TransactionRow = ({ transaction, onViewDetails }) => {
    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle className="text-emerald-500 text-lg" />;
            case 'pending': return <Pending className="text-amber-500 text-lg" />;
            case 'failed': return <Error className="text-rose-500 text-lg" />;
            default: return <Schedule className="text-slate-500 text-lg" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
            case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'failed': return 'bg-rose-100 text-rose-800 border-rose-200';
            default: return 'bg-slate-100 text-slate-800 border-slate-200';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'payment': return <PointOfSale className="text-blue-500 text-sm" />;
            case 'deposit': return <QrCode className="text-green-500 text-sm" />;
            case 'withdrawal': return <Link className="text-purple-500 text-sm" />;
            default: return <Receipt className="text-slate-500 text-sm" />;
        }
    };

    return (
        <tr
            className="border-b border-slate-200 hover:bg-slate-50 transition-all duration-200 cursor-pointer group"
            onClick={() => onViewDetails(transaction)}
        >
            <td className="py-4 pl-6 pr-4">
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
                        {getTypeIcon(transaction.type)}
                    </div>
                    <div>
                        <div className="font-semibold text-slate-900 group-hover:text-amber-600 transition-colors">
                            {transaction.reference}
                        </div>
                        <div className="text-sm text-slate-600">{transaction.description}</div>
                    </div>
                </div>
            </td>
            <td className="py-4 px-4">
                <div className="text-slate-900 font-medium">{transaction.customer}</div>
            </td>
            <td className="py-4 px-4">
                <div className="flex items-center space-x-2">
                    {getStatusIcon(transaction.status)}
                    <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(transaction.status)}`}>
                        {transaction.status === 'completed' ? 'Terminé' :
                            transaction.status === 'pending' ? 'En attente' : 'Échoué'}
                    </span>
                </div>
            </td>
            <td className="py-4 px-4">
                <div className="text-slate-900 font-semibold">
                    {transaction.amount.toLocaleString()} {transaction.currency}
                </div>
            </td>
            <td className="py-4 px-4">
                <div className="text-slate-900">{transaction.date}</div>
                <div className="text-sm text-slate-600">{transaction.time}</div>
            </td>
            <td className="py-4 px-4">
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button className="p-2 text-slate-600 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all duration-200">
                        <Receipt className="text-lg" />
                    </button>
                </div>
            </td>
        </tr>
    );
};


// Page principale des transactions pour le caissier
const CaissierTransactionsPage = () => {
    const { selectedOrganization, user } = useAuth();
    const [activeTab, setActiveTab] = useState('all');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        currency: 'CDF',
        startDate: '',
        endDate: ''
    });
    const [transactionsData, setTransactionsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [accountBalance, setAccountBalance] = useState({ balance: 0, currency: 'CDF' });

    useEffect(() => {
        loadTransactions();
        loadAccountBalance();
    }, [filters.currency, filters.status, filters.search]);

    const loadTransactions = async () => {
        try {
            setLoading(true);
            const { fetchTransactions } = await import('../../services/transactionService');
            const solutionId = localStorage.getItem('netikash_solution_id') || localStorage.getItem('solution_id');
            
            // Récupérer le rôle pour filtrer les transactions
            const currentRole = selectedOrganization?.selectedRole || 'admin';
            const isAdmin = currentRole === 'admin';

            const response = await fetchTransactions({
                own: selectedOrganization?.id || 'all',
                sol: solutionId || 'all',
                srv: 'all',
                act: isAdmin ? 'all' : (user?.id || 'all'),
                ref: 'all',
                opr: 'all',
                slug: filters.search || '',
                crc: filters.currency || 'all',
                sts: filters.status === 'all' ? 'all' : filters.status,
                lmt: 20,
                pge: 1
            });

            // Transformer les transactions pour correspondre au format attendu
            const formattedTransactions = (response.contents || []).map(t => ({
                id: t.id,
                reference: t.code || t.publicRef || t.ref || t.id,
                receiptId: t.receiptId || '',
                raw: t,
                type: t.provider_type?.toLowerCase().includes('deposit') ? 'deposit' : 
                      t.provider_type?.toLowerCase().includes('withdrawal') ? 'withdrawal' : 
                      'payment',
                typeLabel: t.provider_type || 'Transaction',
                description: t.label || t.description || t.ref || 'Transaction',
                customer: t.client_name || t.pos?.payer_name || t.pos?.payer_phone || 'Client',
                amount: Math.abs(t.netAmount || t.amount || 0),
                currency: t.currency || 'CDF',
                status: t.status === 'completed' ? 'completed' : 
                        t.status === 'pending' ? 'pending' : 
                        t.status === 'failed' ? 'failed' : 'pending',
                date: t.state?.requested_at ? new Date(t.state.requested_at).toLocaleDateString('fr-FR') : 
                      t.createdAt ? new Date(t.createdAt).toLocaleDateString('fr-FR') : 
                      new Date().toLocaleDateString('fr-FR'),
                time: t.state?.requested_at ? new Date(t.state.requested_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 
                      t.createdAt ? new Date(t.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 
                      new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                method: resolveOperator({ name: t.provider_name || t.pos?.provider_name, providerId: t.provider_id, phone: t.pos?.payer_phone }) || '—',
                provider: resolveOperator({ name: t.provider_name || t.pos?.provider_name, providerId: t.provider_id, phone: t.pos?.payer_phone }) || '—',
                account: t.buyer?.account || t.seller?.account || '',
                direction: t.direction || 'in',
                fees: t.fees || 0,
                items: t.items || []
            }));

            setTransactionsData(formattedTransactions);
        } catch (error) {
            console.error('Error loading transactions:', error);
            setTransactionsData([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAccountBalance = async () => {
        try {
            const { fetchCashierAccountBalance } = await import('../../services/cashierService');
            const balance = await fetchCashierAccountBalance(filters.currency || 'CDF');
            setAccountBalance(balance);
        } catch (error) {
            console.error('Error loading account balance:', error);
            setAccountBalance({ balance: 0, currency: filters.currency || 'CDF' });
        }
    };

    const filteredTransactions = transactionsData.filter(transaction => {
        // Filtre par statut
        if (filters.status && filters.status !== 'all' && transaction.status !== filters.status) {
            return false;
        }

        // Filtre par recherche
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            return (
                transaction.reference.toLowerCase().includes(searchLower) ||
                String(transaction.receiptId || '').toLowerCase().includes(searchLower) ||
                transaction.description.toLowerCase().includes(searchLower) ||
                transaction.customer.toLowerCase().includes(searchLower) ||
                transaction.amount.toString().includes(searchLower)
            );
        }

        return true;
    });

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="min-h-screen p-6">
                    <div className="space-y-6">
                        {/* En-tête de la page */}
                        <div className="bg-gradient-to-br from-white via-amber-50/30 to-orange-50/20 border-2 border-amber-200/50 rounded-3xl p-6 shadow-xl backdrop-blur-sm">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                <div className="mb-4 md:mb-0">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl shadow-lg">
                                            <SwapHoriz className="text-xl text-white" />
                                        </div>
                                        <div>
                                            <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-900 via-orange-800 to-amber-900 bg-clip-text text-transparent">
                                                Mes Transactions
                                            </h1>
                                            <p className="text-amber-700 mt-1">
                                                Consultez l'historique de toutes vos transactions
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-bold shadow-lg">
                                        {filteredTransactions.length} transaction{filteredTransactions.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Statistiques */}
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
                            {/* Carte Solde Disponible */}
                            <div className="bg-gradient-to-br from-white to-emerald-50/30 rounded-3xl p-6 border-2 border-emerald-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <div className="flex flex-col h-full">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-emerald-700 font-semibold mb-2">Solde Disponible</p>
                                            <p className="text-2xl lg:text-3xl font-bold text-emerald-600 leading-tight break-words">
                                                {accountBalance.balance.toLocaleString()} <span className="text-lg lg:text-xl font-normal">{accountBalance.currency}</span>
                                            </p>
                                        </div>
                                        <div className="p-3 lg:p-4 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl shadow-lg ml-3 flex-shrink-0">
                                            <AccountBalanceWallet className="text-white text-xl lg:text-2xl" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* 4 Cartes de statistiques */}
                            <div className="lg:col-span-4">
                                <TransactionStats transactions={filteredTransactions} />
                            </div>
                        </div>

                        {/* Onglets de navigation */}
                        <TransactionTabs
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                        />

                        {/* Filtres */}
                        <TransactionFilters
                            activeTab={activeTab}
                            onFilterChange={handleFilterChange}
                        />

                        {/* Tableau des transactions */}
                        <div className="bg-white/80 backdrop-blur-xl rounded-3xl border-2 border-amber-200/50 shadow-xl overflow-hidden">
                            {loading ? (
                                <div className="text-center py-16">
                                    <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                    <p className="text-slate-600">Chargement des transactions...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                            <tr className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-b-2 border-amber-200">
                                                <th className="py-5 pl-6 pr-4 text-left text-sm font-bold text-amber-900 uppercase tracking-wider">
                                                    Transaction
                                                </th>
                                                <th className="py-5 px-4 text-left text-sm font-bold text-amber-900 uppercase tracking-wider">
                                                    Client
                                                </th>
                                                <th className="py-5 px-4 text-left text-sm font-bold text-amber-900 uppercase tracking-wider">
                                                    Statut
                                                </th>
                                                <th className="py-5 px-4 text-left text-sm font-bold text-amber-900 uppercase tracking-wider">
                                                    Montant
                                                </th>
                                                <th className="py-5 px-4 text-left text-sm font-bold text-amber-900 uppercase tracking-wider">
                                                    Date
                                                </th>
                                                <th className="py-5 px-4 text-left text-sm font-bold text-amber-900 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200">
                                            {filteredTransactions.map(transaction => (
                                                <TransactionRow
                                                    key={transaction.id}
                                                    transaction={transaction}
                                                    onViewDetails={setSelectedTransaction}
                                                />
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Message si aucune transaction */}
                                    {filteredTransactions.length === 0 && (
                                <div className="text-center py-16">
                                    <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-slate-300">
                                        <SwapHoriz className="text-slate-400 text-3xl" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800 mb-3">
                                        Aucune transaction trouvée
                                    </h3>
                                    <p className="text-slate-600 max-w-md mx-auto mb-6">
                                        Aucune transaction ne correspond à vos critères de recherche.
                                    </p>
                                </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de détails de transaction */}
            {selectedTransaction && (
                <TransactionModal
                    transaction={selectedTransaction}
                    onClose={() => setSelectedTransaction(null)}
                />
            )}
        </div>
    );
};

export default CaissierTransactionsPage;